import React, { useState, useRef, useCallback } from "react";
import { isSet } from "../../../../../../utils/commonUtils";
import FileManagerService from "../../../../../../services/FileManagerService";
import http from "../../../../../../services/http";
import { OK } from "../../../../../../utils/constant";

const DragDropFileUpload = (props) => {
    const {
        label,
        name,
        formData,
        changeHandler,
        defaultValue,
        col,
        validation,
        accept,
        Loader,
        Toast
    } = props;
    const [isHover, setIsHover] = useState(false);
    const [selectedFileName, setSelectedFileName] = useState('');
    const [filePreview, setFilePreview] = useState(null);
    const [fileType, setFileType] = useState(null);
    const fileInputRef = useRef(null);
    
    const value = isSet(formData[name], isSet(defaultValue, null));
    
    // Update selected file name and preview when value changes
    React.useEffect(() => {
        if (value) {
            let fileName = '';
            let preview = null;
            let type = null;
            let parsedValue = value;
            // Try to parse JSON string if value is a string
            if (typeof value === 'string' && value !== '') {
                try {
                    // Check if it looks like JSON
                    if (value.trim().startsWith('{') || value.trim().startsWith('[')) {
                        parsedValue = JSON.parse(value);
                    } else {
                        // If it's just a file_id string, keep it as is
                        parsedValue = value;
                    }
                } catch (e) {
                    // If parsing fails, treat as plain string (file_id)
                    parsedValue = value;
                }
            }
            
            if (parsedValue instanceof File) {
                fileName = parsedValue.name;
                type = parsedValue.type;
                // Create preview for images
                if (parsedValue.type && parsedValue.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        setFilePreview(e.target.result);
                    };
                    reader.readAsDataURL(parsedValue);
                }
            } else if (typeof parsedValue === 'object' && parsedValue !== null) {
                // Handle file object from database
                if (parsedValue.original_name) {
                    fileName = parsedValue.original_name;
                    type = parsedValue.mime_type || parsedValue.type || '';
                    // For images stored in database, fetch and create preview
                    if (parsedValue.file_id && type && type.startsWith('image/')) {
                        // Fetch image preview from server
                        const fetchImagePreview = async () => {
                            try {
                                const response = await FileManagerService.downloadFile(parsedValue.file_id, 'openNewTab');
                                // Note: downloadFile opens in new tab, so we need a different approach
                                // For now, we'll just show the file info and let user click to view
                                setFilePreview(null);
                            } catch (error) {
                                console.error('Error fetching image preview:', error);
                                setFilePreview(null);
                            }
                        };
                        // Don't auto-fetch, let user click to view
                        setFilePreview(null);
                    }
                } else if (parsedValue.name) {
                    fileName = parsedValue.name;
                } else if (parsedValue.file_id) {
                    // If we only have file_id, we can't show the name without fetching file info
                    // For now, show the file_id but this should ideally fetch the file info
                    fileName = parsedValue.file_id;
                }
            } else if (typeof parsedValue === 'string' && parsedValue !== '') {
                // If it's just a file_id string, show it (but ideally should fetch file info)
                fileName = parsedValue;
            }
            
            setSelectedFileName(fileName);
            setFileType(type);
        } else {
            setSelectedFileName('');
            setFilePreview(null);
            setFileType(null);
        }
    }, [value]);

    const handleFileSelect = useCallback((file, additionalParams = {}) => {
        if (file) {
            setSelectedFileName(file.name);
            changeHandler(name, file, additionalParams);
        }
    }, [name, changeHandler]);

    const onDrop = useCallback((e) => {
        e.preventDefault();
        setIsHover(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            handleFileSelect(file, { e });
        }
    }, [handleFileSelect]);

    const onDragOver = (e) => {
        e.preventDefault();
        setIsHover(true);
    };

    const onDragEnter = (e) => {
        e.preventDefault();
        setIsHover(true);
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        setIsHover(false);
    };

    const onChangeHandler = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            handleFileSelect(file, { e });
        }
    };
    //handle remove file
    const handleRemoveFile = (e) => {
        e.stopPropagation();
        setSelectedFileName('');
        setFilePreview(null);
        setFileType(null);
        changeHandler(name, '', {});
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    //handle view file
    const handleViewFile = async (e) => {
        e.stopPropagation();
        if (!value) return;
        let fileId = null;
        let parsedValue = value;
        // Try to parse JSON string if value is a string
        if (typeof value === 'string' && value !== '') {
            try {
                if (value.trim().startsWith('{') || value.trim().startsWith('[')) {
                    parsedValue = JSON.parse(value);
                } else {
                    fileId = value;
                }
            } catch (e) {
                fileId = value;
            }
        }
         // If it's a File object, create object URL and open in new tab
        if (parsedValue instanceof File) {
            const url = URL.createObjectURL(parsedValue);
            // Open in new tab - user can download from there
            const newWindow = window.open(url, '_blank');
            // Clean up the URL after a delay (give time for new tab to load)
            setTimeout(() => {
                if (newWindow) {
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                } else {
                    URL.revokeObjectURL(url);
                }
            }, 100);
        } else {
            if (!fileId) {
                if (parsedValue && typeof parsedValue === 'object' && parsedValue.file_id) {
                    fileId = parsedValue.file_id;
                } else if (typeof parsedValue === 'string') {
                    fileId = parsedValue;
                }
            }
            if (fileId) {
                // If it's a file from database, use FileManagerService to open in new tab
                if (Loader) Loader(true);
                try {
                    // Always open in new tab - user can download from there
                    await FileManagerService.downloadFile(fileId, 'openNewTab');
                } catch (error) {
                    console.error('Error viewing file:', error);
                    if (Toast) Toast.error('Error viewing file');
                } finally {
                    if (Loader) Loader(false);
                }
            }
        }
    };

    const handleDownloadFile = async (e) => {
        e.stopPropagation();
        if (!value) return;
        let fileId = null;
        let parsedValue = value;
        // Try to parse JSON string if value is a string
        if (typeof value === 'string' && value !== '') {
            try {
                if (value.trim().startsWith('{') || value.trim().startsWith('[')) {
                    parsedValue = JSON.parse(value);
                } else {
                    fileId = value;
                }
            } catch (e) {
                fileId = value;
            }
        }
        
        if (parsedValue instanceof File) {
            // Check if it's an image
            const isImage = parsedValue.type && parsedValue.type.startsWith('image/');
            if (isImage) {
                // For images: download as a picture
                const url = URL.createObjectURL(parsedValue);
                const a = document.createElement('a');
                a.href = url;
                a.download = parsedValue.name;
                a.style.display = 'none';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                // Clean up after a short delay
                setTimeout(() => URL.revokeObjectURL(url), 100);
            } else {
                // For documents: open in new tab as blob
                const blobUrl = URL.createObjectURL(parsedValue);
                const newTab = window.open(blobUrl, '_blank');
                // Clean up after a delay to allow loading
                setTimeout(() => {
                    if (newTab) {
                        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                    } else {
                        URL.revokeObjectURL(blobUrl);
                    }
                }, 100);
            }
        } else {
            // Get file_id from parsed value or use the string file_id
            if (!fileId) {
                if (parsedValue && typeof parsedValue === 'object' && parsedValue.file_id) {
                    fileId = parsedValue.file_id;
                } else if (typeof parsedValue === 'string') {
                    fileId = parsedValue;
                }
            }
            
            if (fileId) {
                // Fetch file as blob
                if (Loader) Loader(true);
                try {
                    // Fetch file data as blob
                    const response = await http.GET(`download-files/${fileId}`, { responseType: 'blob' });
                    if (response.code === OK && response.data) {
                        const fileData = response.data;
                        
                        // Decode base64 to binary
                        const binaryString = window.atob(fileData.file_content);
                        const binaryData = new Uint8Array(binaryString.length);
                        for (let i = 0; i < binaryString.length; i++) {
                            binaryData[i] = binaryString.charCodeAt(i);
                        }
                        
                        // Create a blob from the binary data
                        const blob = new Blob([binaryData], { type: fileData.mime_type });
                        const isImage = fileData.mime_type && fileData.mime_type.startsWith('image/');
                        
                        if (isImage) {
                            // For images: download as a picture
                            const blobUrl = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = blobUrl;
                            a.download = fileData.original_name || 'image';
                            a.style.display = 'none';
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            // Clean up after a short delay
                            setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
                        } else {
                            // For documents (PDF, etc.): open in new tab as blob
                            const blobUrl = URL.createObjectURL(blob);
                            const newTab = window.open(blobUrl, '_blank');
                            
                            // Revoke the blob URL after a delay to allow loading
                            setTimeout(() => {
                                if (newTab) {
                                    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                                } else {
                                    URL.revokeObjectURL(blobUrl);
                                }
                            }, 100);
                        }
                    }
                } catch (error) {
                    console.error('Error opening file:', error);
                    if (Toast) Toast.error('Error opening file');
                } finally {
                    if (Loader) Loader(false);
                }
            }
        }
    };

    const isImage = fileType && fileType.startsWith('image/');
    const isPdf = fileType && fileType === 'application/pdf';
    // Check if value has a file_id (either as object property or as string)
    let hasFileId = false;
    if (value) {
        if (value instanceof File) {
            hasFileId = false; // File objects don't have file_id
        } else if (typeof value === 'object' && value !== null && value.file_id) {
            hasFileId = true;
        } else if (typeof value === 'string' && value !== '') {
            // Check if it's a JSON string with file_id, or just a file_id string
            try {
                if (value.trim().startsWith('{')) {
                    const parsed = JSON.parse(value);
                    hasFileId = parsed && parsed.file_id;
                } else {
                    // Assume it's a file_id string if it's not empty
                    hasFileId = true;
                }
            } catch (e) {
                // If parsing fails, assume it's a file_id string
                hasFileId = true;
            }
        }
    }

    return (
        <div className={`${col} pt-2`}>
            {label && (
                <label className="text-sm mb-1 text-dark d-block">{label}</label>
            )}
            <div
                className="rounded p-4 text-center"
                style={{
                    border: `1px dashed ${isHover ? '#000' : '#cdcdcd'}`,
                    backgroundColor: isHover ? '#f8f9fa' : '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                }}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
            >
                <input
                    type="file"
                    id={`${name}Upload`}
                    ref={fileInputRef}
                    className="form-control"
                    style={{ display: 'none' }}
                    onChange={onChangeHandler}
                    accept={accept}
                />
                <label
                    htmlFor={`${name}Upload`}
                    className="mb-0"
                    style={{ cursor: 'pointer' }}
                >
                    <span>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15.9987 21.3337C15.6209 21.3337 15.3045 21.2057 15.0494 20.9497C14.7934 20.6946 14.6654 20.3782 14.6654 20.0004V10.4671L12.1654 12.9671C11.8987 13.2337 11.5876 13.3671 11.232 13.3671C10.8765 13.3671 10.5543 13.2226 10.2654 12.9337C9.9987 12.6671 9.87114 12.3502 9.8827 11.9831C9.89336 11.6168 10.0209 11.3115 10.2654 11.0671L15.0654 6.26706C15.1987 6.13372 15.3431 6.03906 15.4987 5.98306C15.6543 5.92795 15.8209 5.90039 15.9987 5.90039C16.1765 5.90039 16.3431 5.92795 16.4987 5.98306C16.6543 6.03906 16.7987 6.13372 16.932 6.26706L21.732 11.0671C21.9987 11.3337 22.1263 11.6502 22.1147 12.0164C22.104 12.3835 21.9765 12.6893 21.732 12.9337C21.4654 13.2004 21.1489 13.3391 20.7827 13.3497C20.4156 13.3613 20.0987 13.2337 19.832 12.9671L17.332 10.4671V20.0004C17.332 20.3782 17.2045 20.6946 16.9494 20.9497C16.6934 21.2057 16.3765 21.3337 15.9987 21.3337ZM7.9987 26.6671C7.26536 26.6671 6.63781 26.4062 6.11603 25.8844C5.59336 25.3617 5.33203 24.7337 5.33203 24.0004V21.3337C5.33203 20.9559 5.45959 20.6391 5.7147 20.3831C5.9707 20.1279 6.28759 20.0004 6.66536 20.0004C7.04314 20.0004 7.36003 20.1279 7.61603 20.3831C7.87114 20.6391 7.9987 20.9559 7.9987 21.3337V24.0004H23.9987V21.3337C23.9987 20.9559 24.1267 20.6391 24.3827 20.3831C24.6378 20.1279 24.9543 20.0004 25.332 20.0004C25.7098 20.0004 26.0263 20.1279 26.2814 20.3831C26.5374 20.6391 26.6654 20.9559 26.6654 21.3337V24.0004C26.6654 24.7337 26.4045 25.3617 25.8827 25.8844C25.36 26.4062 24.732 26.6671 23.9987 26.6671H7.9987Z" fill="#4A505C"/>
                        </svg>
                    </span>
                    <div className="text-muted text-sm">
                        Drag-and-drop file,or <span className="text-primary">browse computer</span>
                    </div>
                </label>
            </div>
            {selectedFileName && (
                <div className="mt-3">
                    {/* File Preview for Images */}
                    {filePreview && isImage && (
                        <div className="mb-2 text-center position-relative" style={{backgroundColor:"#F0F3FD"}}>
                            <img 
                                src={filePreview} 
                                alt={selectedFileName}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '200px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                                onClick={handleViewFile}
                                title="Click to view in new tab"
                            />
                        </div>
                    )}
                    
                    {/* File Info */}
                    <div className="d-flex align-items-center justify-content-between p-2 rounded" style={{backgroundColor:"#F0F3FD"}}>
                        <div className="d-flex align-items-center flex-grow-1">
                            <i className={`fa ${isImage ? 'fa-image' : isPdf ? 'fa-file-pdf' : 'fa-file'} mr-2`}></i>
                            <span className="text-sm text-dark" title={selectedFileName}>
                                {selectedFileName.length > 30 ? selectedFileName.substring(0, 30) + '...' : selectedFileName}
                            </span>
                        </div>
                        <div className="d-flex align-items-center">
                            {/* View/Download Buttons */}
                            {(hasFileId || value instanceof File) && (
                                <>
                                    {/* View/Open Button - Opens in new tab */}
                                    <button
                                        className="btn btn-sm btn-link text-primary p-1"
                                        onClick={handleViewFile}
                                        title={isImage ? "View in new tab" : "Open in new tab"}
                                    >
                                        <i className="fa fa-external-link"></i>
                                    </button>
                                    {/* Download Button - Available for all file types */}
                                    <button
                                        className="btn btn-sm btn-link text-primary p-1"
                                        onClick={handleDownloadFile}
                                        title="Download file"
                                    >
                                        <i className="fa fa-download"></i>
                                    </button>
                                </>
                            )}
                            {/* Remove Button */}
                            <span
                                className="badge bg-red-soft text-red cursor-pointer rounded-circle ml-1"
                                onClick={handleRemoveFile}
                                title="Remove file"
                            >
                                <i className="fa fa-times" aria-hidden="true"></i>
                            </span>
                        </div>
                    </div>
                </div>
            )}
            {isSet(validation[name], "") !== "" && (
                <span className="text-danger m-1 d-block">
                    {isSet(validation[name], "")}
                </span>
            )}
        </div>
    );
};

DragDropFileUpload.defaultProps = {
    defaultValue: null,
    accept: '*/*',
    validation: {},
    Loader: null,
    Toast: null
};

export default DragDropFileUpload;

